import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { GetHistoricalUsageByBillCyclePayload } from '@mfe/shared/schema-types';

import { FetchWithErrorsQuery } from '../graphql';

import {
  BillCycleDuration,
  BillCycles,
  parseBillCycles,
  UnleashedTimeInterval,
  UsageData,
} from './utils';

export type HistoricalUsageInfoState = {
  loading: boolean;
  billCycles: BillCycles;
  billCycleDurations: BillCycleDuration[] | [];
  errors: FetchWithErrorsQuery['errors'] | null;
  unleashedUsage: {
    [UnleashedTimeInterval.LAST_30_DAYS]: UsageData[];
    [UnleashedTimeInterval.LAST_60_DAYS]: UsageData[];
    [UnleashedTimeInterval.LAST_90_DAYS]: UsageData[];
  };
};

const initialState: HistoricalUsageInfoState = {
  loading: true,
  billCycles: {},
  billCycleDurations: [],
  errors: null,
  unleashedUsage: {
    [UnleashedTimeInterval.LAST_30_DAYS]: [],
    [UnleashedTimeInterval.LAST_60_DAYS]: [],
    [UnleashedTimeInterval.LAST_90_DAYS]: [],
  },
};

type SetHistoricalUsageInfoAction = PayloadAction<
  Partial<GetHistoricalUsageByBillCyclePayload>
>;

type TimeIntervalInDays = `${UnleashedTimeInterval}`;

export type FetchUnleashedUsageAction = PayloadAction<{
  start: string;
  end: string;
  selectedInterval: TimeIntervalInDays;
}>;

type SetUnleashedUsageAction = PayloadAction<{
  usageData: UsageData[] | [];
  interval: TimeIntervalInDays;
}>;

export const historicalUsageInfoSlice = createSlice({
  name: 'historicalUsageInfo',
  initialState,
  reducers: {
    setApiErrors: (state, { payload }) => {
      state.loading = false;
      state.errors = payload;
    },
    setHistoricalUsageInfo: (
      state,
      { payload }: { payload: SetHistoricalUsageInfoAction['payload'] }
    ) => {
      state.loading = false;
      state.errors = null;

      const processedData = parseBillCycles(payload, state);
      state.billCycles = processedData.billCycles;
      state.billCycleDurations = processedData.billCycleDurations;
    },
    fetchHistoricalUsageInfo: (state) => {
      state.loading = true;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchUnleashedUsage: (state, action: FetchUnleashedUsageAction) => {
      state.loading = true;
    },
    setUnleashedUsageNotLoading: (state) => {
      state.loading = false;
    },
    setUnleashedUsage: (state, action: SetUnleashedUsageAction) => {
      state.loading = false;
      state.unleashedUsage[action.payload.interval] = action.payload.usageData;
    },
  },
});

export const {
  setHistoricalUsageInfo,
  fetchHistoricalUsageInfo,
  fetchUnleashedUsage,
  setUnleashedUsage,
  setUnleashedUsageNotLoading,
  setApiErrors,
} = historicalUsageInfoSlice.actions;

export const selectHistoricalUsageInfo = (state: {
  historicalUsageInfo: HistoricalUsageInfoState;
}) => state.historicalUsageInfo;
